import Markdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"

interface MarkdownContentProps {
  children: string
}

const components: Components = {
  // Downgrade heading sizes for chat bubbles
  h1: ({ children }) => (
    <h3 className="text-lg font-bold mt-3 mb-1">{children}</h3>
  ),
  h2: ({ children }) => (
    <h4 className="text-base font-bold mt-2 mb-1">{children}</h4>
  ),
  h3: ({ children }) => (
    <h5 className="text-base font-semibold mt-2 mb-1">{children}</h5>
  ),
  // Code blocks with contrast against the dark gradient bubble bg
  code: ({ className, children }) => {
    const isBlock = className?.includes("language-")
    if (isBlock) {
      return <code className={`${className ?? ""} text-xs`}>{children}</code>
    }
    return (
      <code className="bg-base-300 px-1 py-0.5 rounded text-xs">
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="bg-base-300 rounded p-2 overflow-x-auto my-1 text-xs">
      {children}
    </pre>
  ),
  // Links use DaisyUI info color
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-info underline"
    >
      {children}
    </a>
  ),
  // Tables match DataTable styling
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="table table-xs table-zebra">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="capitalize text-xs">{children}</th>,
  td: ({ children }) => <td className="text-xs">{children}</td>,
  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-base-content/30 pl-3 my-1 opacity-80">
      {children}
    </blockquote>
  ),
}

export function MarkdownContent({ children }: MarkdownContentProps) {
  return (
    <div className="prose max-w-none prose-invert text-base leading-relaxed">
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </Markdown>
    </div>
  )
}
